import { Currency, Money } from "@hubrise/react-components"

import {
  CategoryId,
  ChargeId,
  DealId,
  DealLineId,
  DealLineSkuId,
  DiscountId,
  OptionId,
  OptionListId,
  ProductId,
  SkuId,
  VariantId,
} from "./ids"
import {
  Additive,
  Allergen,
  DealLinePricing,
  DiscountPricing,
  EnergyUnit,
  ICatalog,
  ICategory,
  ICharge,
  IDeal,
  IDealLine,
  IDealLineSku,
  IdGenerator,
  IDiscount,
  INutrition,
  IOption,
  IOptionList,
  IPer100Values,
  IPriceOverride,
  IProduct,
  IRestrictions,
  ISku,
  IVariant,
  LEGACY_ALLERGEN_TAGS,
  LegacyAllergenTag,
  Per100Per,
  Volume,
  Weight,
} from "./models"
import { renumberCatalog } from "./utils/Catalog"

export interface ImportedCatalog {
  id: string
  name: string
  data: {
    variants: Array<ImportedVariant>
    categories: Array<ImportedCategory>
    products: Array<ImportedProduct>
    option_lists: Array<ImportedOptionList>
    deals: Array<ImportedDeal>
    discounts: Array<ImportedDiscount>
    charges: Array<ImportedCharge>
  }
}

interface ImportedVariant {
  ref: string
  name: string
}

interface ImportedRestrictions {
  enabled?: boolean
  variant_refs?: Array<string>
  dow?: string
  start_time?: string
  end_time?: string
  start_date?: string
  end_date?: string
  service_types?: Array<string>
  service_type_refs?: Array<string>
  min_order_amount?: string
  max_per_order?: number
  max_per_customer?: number
}

interface ImportedPriceOverride {
  variant_refs?: Array<string>
  dow?: string
  start_time?: string
  end_time?: string
  start_date?: string
  end_date?: string
  service_types?: Array<string>
  service_type_refs?: Array<string>
  price: string
}

interface ImportedCategory {
  id: string
  ref: string
  parent_id: string | null
  name: string
  description: string | null
  tags: Array<string>
  image_ids: Array<string>
}

interface ImportedProduct {
  id: string
  ref: string | null
  category_id: string
  name: string
  description: string | null
  tags: Array<string>
  nutrition: ImportedNutrition | null
  tax_rate: {
    delivery: string
    collection: string
    eat_in: string
  } | null
  image_ids: Array<string>
  skus: Array<ImportedSku>
}

interface ImportedPer100Energy {
  unit: string
  lower: string
  upper: string | null
}

interface ImportedPer100Values {
  per: string
  energy: ImportedPer100Energy | null
  fat: string | null
  saturated_fat: string | null
  mono_unsaturated_fat: string | null
  poly_unsaturated_fat: string | null
  carbohydrate: string | null
  sugar: string | null
  polyols: string | null
  starch: string | null
  fibre: string | null
  protein: string | null
  salt: string | null
}

interface ImportedNutrition {
  legal_name: string | null
  ingredients: string[] | null
  additives: string[] | null
  allergens: string[] | null
  per_100_values: ImportedPer100Values | null
  alcohol_percentage: string | null
  caffeine_mg_per_100ml: string | null
  producer_name: string | null
  producer_brand: string | null
  producer_address: string | null
  country_of_origin: string | null
}

interface ImportedSku {
  id: string
  ref: string | null
  name: string | null
  restrictions: ImportedRestrictions
  product_id: string
  price: string
  price_overrides: Array<ImportedPriceOverride>
  option_list_ids: Array<string>
  tags: Array<string>
  barcodes: Array<string>
  weight: string | null
  volume: string | null
  nb_servings: number | null
}

interface ImportedOptionList {
  id: string
  ref: string
  name: string
  min_selections: number
  max_selections: number | null
  multiple_selection: boolean
  tags: Array<string>
  options: Array<ImportedOption>
}

interface ImportedOption {
  id: string
  ref: string | null
  name: string
  restrictions: ImportedRestrictions
  price: string
  price_overrides: Array<ImportedPriceOverride>
  default: boolean
  tags: Array<string>
  nutrition: ImportedNutrition | null
  weight: string | null
  volume: string | null
}

interface ImportedDeal {
  id: string
  ref: string | null
  category_id: string | null
  name: string
  description: string | null
  restrictions: ImportedRestrictions
  coupon_codes: Array<string>
  tags: Array<string>
  image_ids: Array<string>
  lines: Array<ImportedDealLine>
}

interface ImportedDealLine {
  label: string | null
  pricing_effect: string
  pricing_value: string | null
  skus: Array<{
    id: string
    extra_charge: string | null
  }>
}

interface ImportedDiscount {
  ref: string | null
  name: string
  description: string | null
  restrictions: ImportedRestrictions
  coupon_codes: Array<string>
  pricing_effect: string
  pricing_value: string
  image_ids: Array<string>
}

interface ImportedCharge {
  ref: string | null
  name: string
  type: string
  restrictions: ImportedRestrictions
  price: string | null
}

/** Category HubRise id -> internal id */
type CategoryIdMapping = Map<string, CategoryId>

/** Option list HubRise id -> internal id */
type OptionListIdMapping = Map<string, OptionListId>

/** Sku HubRise id -> internal id */
type SkuIdMapping = Map<string, SkuId>

const importVariants = (catalog: ImportedCatalog, idGenerator: IdGenerator): Array<IVariant> => {
  return catalog.data.variants.map((variant) => {
    return {
      id: VariantId(idGenerator()),
      ref: variant.ref,
      name: variant.name,
    }
  })
}

const importCategories = (
  catalog: ImportedCatalog,
  idGenerator: IdGenerator,
): [Array<ICategory>, CategoryIdMapping] => {
  const idMapping: CategoryIdMapping = new Map<string, CategoryId>()

  const categories = catalog.data.categories.map((importedCategory) => {
    const { id, parent_id, name, description, tags, image_ids } = importedCategory

    const internalId = CategoryId(idGenerator())
    idMapping.set(id, internalId)
    const parentId = parent_id === null ? null : idMapping.get(parent_id)
    if (parentId === undefined) throw `Category id ${parent_id} does not exist`

    const category: ICategory = {
      // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
      id: internalId,
      parentId,
      name,
      description: description ?? "",
      tags,
      imageHrIds: image_ids,
    }
    return category
  })

  return [categories, idMapping]
}

const importOptionLists = (
  catalog: ImportedCatalog,
  idGenerator: IdGenerator,
): [Array<IOptionList>, OptionListIdMapping] => {
  const idMapping: OptionListIdMapping = new Map() as OptionListIdMapping

  const optionLists = catalog.data.option_lists.map((importedOptionList) => {
    const { id: hubriseId, name, min_selections, max_selections, multiple_selection, tags } = importedOptionList

    const id = OptionListId(idGenerator())
    idMapping.set(hubriseId, id)
    const options = importOptions(importedOptionList, idGenerator)

    const optionList: IOptionList = {
      // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
      id,
      name,
      min_selections,
      max_selections,
      multiple_selection,
      tags,
      options,
    }
    return optionList
  })

  return [optionLists, idMapping]
}

const importRestrictions = (importedRestrictions: ImportedRestrictions): IRestrictions => {
  const {
    enabled,
    variant_refs,
    dow,
    start_time,
    end_time,
    start_date,
    end_date,
    service_types,
    service_type_refs,
    min_order_amount,
    max_per_order,
    max_per_customer,
  } = importedRestrictions
  return {
    enabled: enabled ?? true,
    variantRefs: variant_refs,
    dow,
    startTime: start_time,
    endTime: end_time,
    startDate: start_date,
    endDate: end_date,
    serviceTypes: service_types,
    serviceTypeRefs: service_type_refs,
    minOrderAmount: min_order_amount,
    maxPerOrder: max_per_order,
    maxPerCustomer: max_per_customer,
  }
}

const importPriceOverrides = (priceOverrides: Array<ImportedPriceOverride>): Array<IPriceOverride> => {
  return priceOverrides.map((priceOverride) => {
    const { variant_refs, dow, start_time, end_time, start_date, end_date, service_types, service_type_refs } =
      priceOverride
    return {
      variantRefs: variant_refs,
      dow,
      startTime: start_time,
      endTime: end_time,
      startDate: start_date,
      endDate: end_date,
      serviceTypes: service_types,
      serviceTypeRefs: service_type_refs,
      price: Money.fromString(priceOverride.price),
    }
  })
}

const importNutrition = (nutrition: ImportedNutrition | null): INutrition | null => {
  if (!nutrition) return null
  return {
    ...nutrition,
    additives: nutrition.additives as Additive[] | null,
    allergens: nutrition.allergens as Allergen[] | null,
    per_100_values: nutrition.per_100_values
      ? {
          ...(nutrition.per_100_values as unknown as IPer100Values),
          per: nutrition.per_100_values.per as Per100Per,
          energy: nutrition.per_100_values.energy
            ? {
                unit: nutrition.per_100_values.energy.unit as EnergyUnit,
                lower: Number(nutrition.per_100_values.energy.lower),
                upper:
                  nutrition.per_100_values.energy.upper !== null ? Number(nutrition.per_100_values.energy.upper) : null,
              }
            : null,
        }
      : null,
    alcohol_percentage: nutrition.alcohol_percentage !== null ? Number(nutrition.alcohol_percentage) : null,
    caffeine_mg_per_100ml: nutrition.caffeine_mg_per_100ml !== null ? Number(nutrition.caffeine_mg_per_100ml) : null,
  }
}

const importOptions = (importedOptionList: ImportedOptionList, idGenerator: IdGenerator): Array<IOption> => {
  return importedOptionList.options.map((importedOption) => {
    const { ref, name, restrictions, price, price_overrides, tags, nutrition, weight, volume } = importedOption

    const option: IOption = {
      id: OptionId(idGenerator()),
      ref: ref ?? "",
      name,
      restrictions: importRestrictions(restrictions),
      price: Money.fromString(price),
      priceOverrides: importPriceOverrides(price_overrides),
      isDefault: importedOption.default,
      tags,
      nutrition: importNutrition(nutrition),
      weight: weight as Weight | null,
      volume: volume as Volume | null,
    }
    return option
  })
}

const importProducts = (
  catalog: ImportedCatalog,
  idGenerator: IdGenerator,
  categoryIdMapping: CategoryIdMapping,
  optionListIdMapping: OptionListIdMapping,
): [Array<IProduct>, SkuIdMapping] => {
  const idMapping: SkuIdMapping = new Map() as SkuIdMapping

  const products: Array<IProduct> = catalog.data.products.map((importedProduct) => {
    const { category_id: hubriseId, name, description, tags, image_ids } = importedProduct

    const id = ProductId(idGenerator())
    const categoryId = categoryIdMapping.get(hubriseId)
    if (categoryId === undefined) throw `Category id ${hubriseId} does not exist`

    const legacyAllergenTags = tags.filter((t): t is LegacyAllergenTag =>
      (LEGACY_ALLERGEN_TAGS as ReadonlyArray<string>).includes(t),
    )
    const filteredTags = tags.filter((t) => !(LEGACY_ALLERGEN_TAGS as ReadonlyArray<string>).includes(t))

    const skus = importSkus(importedProduct, idGenerator, id, optionListIdMapping, idMapping)

    const product: IProduct = {
      // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
      id,
      categoryId,
      name,
      description: description ?? "",
      tags: filteredTags,
      legacyAllergenTags,
      nutrition: importNutrition(importedProduct.nutrition),
      taxRate: importedProduct.tax_rate
        ? {
            delivery: Number(importedProduct.tax_rate.delivery),
            collection: Number(importedProduct.tax_rate.collection),
            eatIn: Number(importedProduct.tax_rate.eat_in),
          }
        : null,
      imageHrIds: image_ids,
      skus,
    }
    return product
  })

  return [products, idMapping]
}

const importSkus = (
  product: ImportedProduct,
  idGenerator: IdGenerator,
  productId: ProductId,
  optionListIdMapping: OptionListIdMapping,
  skuIdMapping: SkuIdMapping,
): Array<ISku> => {
  return product.skus.map((importedSku) => {
    const { ref, name, restrictions, price, price_overrides, option_list_ids, tags, barcodes, weight, volume } =
      importedSku

    const id = SkuId(idGenerator())
    skuIdMapping.set(importedSku.id, id)

    const optionListIds: Array<OptionListId> = option_list_ids.map((option_list_id) => {
      const optionListId = optionListIdMapping.get(option_list_id)
      if (optionListId === undefined) throw `Option list id ${option_list_id} does not exist`
      return optionListId
    })

    const sku: ISku = {
      id,
      ref: ref ?? "",
      name: name ?? "",
      restrictions: importRestrictions(restrictions),
      price: Money.fromString(price),
      priceOverrides: importPriceOverrides(price_overrides),
      optionListIds,
      tags,
      barcodes,
      weight: weight as Weight | null,
      volume: volume as Volume | null,
      nbServings: importedSku.nb_servings ?? null,
    }
    return sku
  })
}

const importDeals = (
  catalog: ImportedCatalog,
  idGenerator: IdGenerator,
  categoryIdMapping: CategoryIdMapping,
  skuIdMapping: SkuIdMapping,
): Array<IDeal> => {
  return catalog.data.deals.map((importedDeal) => {
    const {
      ref,
      category_id: categoryHubriseId,
      name,
      description,
      restrictions,
      coupon_codes,
      tags,
      image_ids,
    } = importedDeal

    let categoryId: CategoryId | null = null
    if (categoryHubriseId !== null) {
      categoryId = categoryIdMapping.has(categoryHubriseId) ? categoryIdMapping.get(categoryHubriseId)! : null
    }

    const lines: Array<IDealLine> = importDealLines(importedDeal, idGenerator, skuIdMapping)

    const deal: IDeal = {
      // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
      id: DealId(idGenerator()),
      ref: ref ?? "",
      categoryId,
      name,
      description: description ?? "",
      restrictions: importRestrictions(restrictions),
      couponCodes: coupon_codes,
      tags,
      imageHrIds: image_ids,
      lines,
    }
    return deal
  })
}

const importDealLines = (
  importedDeal: ImportedDeal,
  idGenerator: IdGenerator,
  skuIdMapping: SkuIdMapping,
): Array<IDealLine> => {
  return importedDeal.lines.map((importedDealLine) => {
    const { label, pricing_effect, pricing_value, skus: importedSkus } = importedDealLine

    const skus: Array<IDealLineSku> = importedSkus.map(({ id: skuHubriseId, extra_charge }) => {
      const skuId = skuIdMapping.get(skuHubriseId)
      if (skuId === undefined) throw `Sku id ${skuId} does not exist`
      return {
        id: DealLineSkuId(idGenerator()),
        skuId,
        extraCharge: extra_charge === null ? null : Money.fromString(extra_charge),
      }
    })

    const pricing = importDealLinePricing({ pricing_effect, pricing_value })

    const dealLine: IDealLine = {
      id: DealLineId(idGenerator()),
      label: label === null ? "" : label,
      ...pricing,
      skus,
    }
    return dealLine
  })
}

const importDealLinePricing = (
  importedPricing: Pick<ImportedDealLine, "pricing_effect" | "pricing_value">,
): DealLinePricing => {
  const { pricing_effect, pricing_value } = importedPricing
  switch (pricing_effect) {
    case "unchanged":
      return { pricingEffect: pricing_effect, pricingValue: null }
    case "fixed_price":
    case "price_off":
      return { pricingEffect: pricing_effect, pricingValue: Money.fromString(pricing_value!) }
    case "percentage_off":
      return { pricingEffect: pricing_effect, pricingValue: Number(pricing_value!) }
    default:
      throw `Unexpected pricing_effect ${pricing_effect}`
  }
}

const importDiscounts = (catalog: ImportedCatalog, idGenerator: IdGenerator): Array<IDiscount> => {
  return catalog.data.discounts.map((importedDiscount) => {
    const { ref, name, description, restrictions, coupon_codes, pricing_effect, pricing_value, image_ids } =
      importedDiscount

    const pricing = importDiscountPricing({ pricing_effect, pricing_value })

    const discount: IDiscount = {
      // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
      id: DiscountId(idGenerator()),
      ref: ref ?? "",
      name,
      description: description ?? "",
      restrictions: importRestrictions(restrictions),
      couponCodes: coupon_codes,
      ...pricing,
      imageHrIds: image_ids,
    }
    return discount
  })
}

const importDiscountPricing = (
  importedPricing: Pick<ImportedDiscount, "pricing_effect" | "pricing_value">,
): DiscountPricing => {
  const { pricing_effect, pricing_value } = importedPricing
  switch (pricing_effect) {
    case "price_off":
      return { pricingEffect: pricing_effect, pricingValue: Money.fromString(pricing_value) }
    case "percentage_off":
      return { pricingEffect: pricing_effect, pricingValue: Number(pricing_value) }
    default:
      throw `Unexpected pricing_effect ${pricing_effect}`
  }
}

const importCharges = (catalog: ImportedCatalog, idGenerator: IdGenerator): Array<ICharge> => {
  return catalog.data.charges.map((importedCharge) => {
    const { ref, name, type, restrictions, price } = importedCharge

    if (!(type === "delivery" || type === "payment_fee" || type === "tip" || type === "tax" || type === "other"))
      throw `Unexpected type ${type}. Was expecting "delivery", "payment_fee", "tip", "tax" or "other".`

    const charge: ICharge = {
      // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
      id: ChargeId(idGenerator()),
      ref: ref ?? "",
      name,
      type,
      restrictions: importRestrictions(restrictions),
      price: price === null ? null : Money.fromString(price),
    }
    return charge
  })
}

const importCatalog = (catalog: ImportedCatalog, currency: Currency): ICatalog => {
  let id = 0
  const idGenerator: IdGenerator = () => id++

  const variants = importVariants(catalog, idGenerator)
  const [categories, categoryIdMapping] = importCategories(catalog, idGenerator)
  const [optionLists, optionListIdMapping] = importOptionLists(catalog, idGenerator)
  const [products, skuIdMapping] = importProducts(catalog, idGenerator, categoryIdMapping, optionListIdMapping)
  const deals = importDeals(catalog, idGenerator, categoryIdMapping, skuIdMapping)
  const discounts = importDiscounts(catalog, idGenerator)
  const charges = importCharges(catalog, idGenerator)

  return renumberCatalog({
    idGenerator,
    hrId: catalog.id,
    name: catalog.name,
    currency,
    data: {
      variants,
      categories,
      products,
      optionLists,
      deals,
      discounts,
      charges,
    },
  })
}

export default (catalog: ImportedCatalog, currency: Currency): ICatalog => importCatalog(catalog, currency)
